export interface TemplateData {
  l1: string;
  /** l2 中 {N} 之前的文案 */
  l2Prefix: string;
  /** {N} 后紧跟的量词/名词，如 things、signals、quiet flags */
  l2Quantifier: string;
  /** 量词之后的剩余 l2 文案 */
  l2Suffix: string;
  l3: string;
  templateId: number;
}

const L1A = [
  "Hi, {name}",
  "Welcome back, {name}",
  "Hey, {name}",
  "Good to see you, {name}",
  "Hi there, {name}",
];
const L1B = [
  "Welcome back",
  "Hi there",
  "Good to see you",
  "Hey",
  "Hello again",
];

const L23_PAIRS: { l2: string; l3: string }[] = [
  {
    l2: "Ed handled {N} things in your absence.",
    l3: "Reads through everything you don't have time to and surfaces only what actually matters.",
  },
  {
    l2: "{N} things stood out today.",
    l3: "The market kept moving while you stepped away — Ed didn't miss the moments that count.",
  },
  {
    l2: "Ed pulled {N} things from the noise.",
    l3: "Filters the day's chatter and keeps only what could move the needle for your portfolio.",
  },
  {
    l2: "Ed saved {N} signals for your return.",
    l3: "Stayed put while you stepped away, listening for the things you'd want to know about.",
  },
  {
    l2: "{N} moments worth your eyes today.",
    l3: "Ed watched the day on your behalf and set aside only what's worth a closer look.",
  },
  {
    l2: "Ed flagged {N} updates for you.",
    l3: "Read between the lines while you were gone and kept only what could matter to you.",
  },
  {
    l2: "{N} signals made it through the filter.",
    l3: "Ed sat through a busy day so you didn't have to — only the meaningful made the cut.",
  },
  {
    l2: "Ed noted {N} things in your absence.",
    l3: "Markets shuffled, headlines came and went — keeping what could shape your next decision.",
  },
  {
    l2: "{N} things came up while you were out.",
    l3: "Ed read everything you didn't have time to and held on to what actually matters here.",
  },
  {
    l2: "Ed has {N} things to share with you.",
    l3: "Pulled out of a noisy day so you see only what could actually move things for your money.",
  },
  {
    l2: "{N} updates lined up for your return.",
    l3: "Ed watched closely while you stepped away, holding on to anything worth knowing.",
  },
  {
    l2: "Ed set aside {N} moments for you.",
    l3: "Read through the day's noise so only what could shape your portfolio lands in front of you.",
  },
  {
    l2: "{N} things worth your attention today.",
    l3: "Ed combed through everything you didn't have time to and kept what could matter to you.",
  },
  {
    l2: "Ed kept {N} signals close for your return.",
    l3: "Watched while you were elsewhere, holding on to the things worth a moment of your time.",
  },
  {
    l2: "{N} things made the cut today.",
    l3: "Ed read the noise so you don't have to, surfacing only what could shape your next move.",
  },
  {
    l2: "Ed has {N} quiet flags for you.",
    l3: "Nothing urgent — just a few things worth knowing when you have a moment to look.",
  },
  {
    l2: "{N} things stood out worth a look.",
    l3: "Ed paid attention while you stepped away, holding on to what could actually matter.",
  },
  {
    l2: "Ed found {N} moments worth your time.",
    l3: "Read everything you didn't have time to and kept only what matters for your portfolio.",
  },
  {
    l2: "{N} things came up in your absence.",
    l3: "Ed sifted through the day's noise on your behalf — keeping what could shape what's next.",
  },
  {
    l2: "Ed pulled {N} signals from the noise.",
    l3: "Stayed with the market while you were gone, holding on to what could matter for you.",
  },
];

/** {N} 后可能为多词的量词（需先于单 token 匹配） */
const L2_MULTI_WORD_QUANTIFIERS = ["quiet flags"] as const;

const PLACEHOLDER_N = "{N}";

/**
 * 将 l2 模板拆为：前缀 | 量词 | 后缀（量词为 {N} 后紧跟的名词短语）
 */
const splitL2Template = (
  l2Template: string,
): { prefix: string; quantifier: string; suffix: string } => {
  const nIdx = l2Template.indexOf(PLACEHOLDER_N);
  if (nIdx === -1) {
    return { prefix: l2Template, quantifier: "things", suffix: "" };
  }

  const prefix = l2Template.slice(0, nIdx);
  const afterN = l2Template.slice(nIdx + PLACEHOLDER_N.length);

  for (const multi of L2_MULTI_WORD_QUANTIFIERS) {
    if (afterN.startsWith(` ${multi}`)) {
      return {
        prefix,
        quantifier: multi,
        suffix: afterN.slice(` ${multi}`.length),
      };
    }
    if (afterN.startsWith(multi)) {
      return {
        prefix,
        quantifier: multi,
        suffix: afterN.slice(multi.length),
      };
    }
  }

  const match = afterN.match(/^(\s*)(\S+)([\s\S]*)$/);
  if (!match) {
    return { prefix, quantifier: "things", suffix: afterN };
  }

  return {
    prefix,
    quantifier: match[2],
    suffix: match[3],
  };
};

/** 渐变高亮段：滚动数字 + 模板量词 */

export const getRandomTemplate = (
  userName: string | undefined,
  _itemCount: number,
): TemplateData => {
  const l1Pool = userName ? L1A : L1B;
  const l1 = l1Pool[Math.floor(Math.random() * l1Pool.length)].replace(
    "{name}",
    userName || "",
  );

  const pairIndex = Math.floor(Math.random() * L23_PAIRS.length);
  const pair = L23_PAIRS[pairIndex];
  const { prefix, quantifier, suffix } = splitL2Template(pair.l2);

  return {
    l1,
    l2Prefix: prefix,
    l2Quantifier: quantifier,
    l2Suffix: suffix,
    l3: pair.l3,
    templateId: pairIndex + 1,
  };
};
